import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Avatar, Typography, List, Grid, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, deleteUser } from '@/api/users';
import UserModal from '@/components/features/users/UserModal';
import { useAuth } from '@/contexts/AuthContext';
import type { User } from '@/types/users';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { UserOutlined, EyeOutlined } from '@ant-design/icons';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';

const { Text } = Typography;
const { useBreakpoint } = Grid;
const PAGE_SIZE = 10;

export default function UsersPage() {
  const { t } = useTranslation(['users', 'common']);
  const navigate = useNavigate();
  const { firestoreUser, workspaceStats } = useAuth();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [lastDocs, setLastDocs] = useState<QueryDocumentSnapshot<DocumentData>[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const screens = useBreakpoint();
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', currentPage, pageSize, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser?.workspace?.id) return { users: [], lastDoc: null, total: 0 };
      const lastDoc = currentPage > 1 ? lastDocs[currentPage - 2] : undefined;
      
      console.log('Fetching users:', { currentPage, pageSize, lastDoc, lastDocs: lastDocs.length });
      
      return getUsers({
        workspaceId: firestoreUser.workspace.id,
        pageSize,
        lastDoc,
        firestoreUser
      });
    },
    enabled: !!firestoreUser?.workspace?.id
  });

  // Update lastDocs when new data arrives
  React.useEffect(() => {
    if (usersData?.lastDoc) {
      setLastDocs(prev => {
        const newLastDocs = [...prev];
        newLastDocs[currentPage - 1] = usersData.lastDoc!;
        return newLastDocs;
      });
    }
  }, [usersData?.lastDoc, currentPage]);

  // Reset lastDocs when page size changes
  React.useEffect(() => {
    setLastDocs([]);
    setCurrentPage(1);
  }, [pageSize]);

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteUser(userId, firestoreUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success(t('users:messages.deleteSuccess'));
    },
    onError: (error: Error) => {
      message.error(error.message || t('users:messages.deleteFailed'));
    }
  });

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setSelectedUser(undefined);
    setIsModalOpen(false);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    handleModalClose();
  };

  const formatDate = (date: any) => {
    if (!date) return '-';
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString();
    }
    return '-';
  };

  const handleDeleteClick = (id: string) => {
    setDeleteUserId(id);
  };

  const handleConfirmDelete = async () => {
    if (deleteUserId) {
      await handleDelete(deleteUserId);
      setDeleteUserId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteUserId(null);
  };

  const columns = [
    {
      title: t('users:fields.name'),
      dataIndex: 'displayName',
      key: 'name',
    },
    {
      title: t('users:fields.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('users:fields.username'),
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: t('users:fields.status'),
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: t('users:fields.roles'),
      dataIndex: 'roles',
      key: 'roles',
      render: (roles: string[]) => roles?.join(', '),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: User) => (
        <Space>
          <Button onClick={() => navigate(`/users/${record.id}`, { state: { user: record } })}>
            {t('common:actions.view')}
          </Button>
          <Button onClick={() => handleEdit(record)}>
            {t('common:actions.edit')}
          </Button>
          <Button danger onClick={() => handleDeleteClick(record.id)}>
            {t('common:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  };

  const renderMobileView = () => (
    <List
      dataSource={usersData?.users || []}
      loading={isLoading}
      itemLayout="vertical"
      renderItem={(user: User) => (
        <List.Item
          key={user.id}
          actions={[
            <Button 
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/users/${user.id}`, { state: { user: user } })}
            >
              {t('common:actions.view')}
            </Button>
          ]}
        >
          <List.Item.Meta
            avatar={
              <Avatar 
                size={64}
                src={user.profilePicture?.url} 
                icon={<UserOutlined />}
              />
            }
            title={
              <Space direction="vertical" size={0}>
                <Text strong>{user.displayName || '-'}</Text>
                <Text type="secondary">{user.email}</Text>
              </Space>
            }
            description={
              <Space direction="vertical" size={4} style={{ marginTop: 8 }}>
                <Space>
                  <Tag color={user.status === 'active' ? 'green' : 'red'}>
                    {t(`users:status.${user.status}`)}
                  </Tag>
                  {user.roles.map(role => (
                    <Tag key={role} color="blue">
                      {t(`users:roles.${role}`)}
                    </Tag>
                  ))}
                </Space>
                <Text type="secondary">
                  {t('users:fields.lastLogin')}: {formatDate(user.lastLogin)}
                </Text>
              </Space>
            }
          />
        </List.Item>
      )}
    />
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          {t('users:actions.add')}
        </Button>
      </div>

      <Card
        title={t('users:title')}
      >
        {!screens.md ? (
          renderMobileView()
        ) : (
          <Table
            columns={columns}
            dataSource={usersData?.users || []}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: workspaceStats?.totalUsers || 0,
              onChange: handlePageChange,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              showTotal: (total: number, range: [number, number]) => 
                t('common:pagination.showing', { 
                  start: range[0], 
                  end: range[1], 
                  total 
                })
            }}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Card>

      <UserModal
        visible={isModalOpen}
        onCancel={handleModalClose}
        onSuccess={handleModalSuccess}
        user={selectedUser}
      />

      <DeleteConfirmationModal
        visible={!!deleteUserId}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('users:confirmDeleteTitle')}
      />
    </div>
  );
}; 